/**
 * Redux Store Configuration
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import * as Sentry from "@sentry/react";

// Import slices
import authReducer from "./slices/authSlice";
import userReducer from "./slices/userSlice";
import meetingReducer from "./slices/meetingSlice";
import meetingModulesReducer from "./slices/meetingModulesSlice";
import meetingSectionReducer from "./slices/meetingSectionSlice";
import realtimeConversationReducer from "./slices/realtimeConversationSlice";
import interviewAssessmentReducer from "./slices/interviewAssessmentSlice";
import dashboardReducer from "./slices/dashboardSlice";
import teacherProfileReducer from "./slices/teacherProfileSlice";
import uiReducer from "./slices/uiSlice";
import subscriptionReducer from "./slices/subscriptionSlice";
import adminReducer from "./slices/adminSlice";
import quizReducer from "./slices/quizSlice";

/**
 * Combine all reducers
 */
const rootReducer = combineReducers({
  auth: authReducer,
  user: userReducer,
  meeting: meetingReducer,
  meetingModules: meetingModulesReducer,
  meetingSection: meetingSectionReducer,
  realtimeConversation: realtimeConversationReducer,
  interviewAssessment: interviewAssessmentReducer,
  dashboard: dashboardReducer,
  teacherProfile: teacherProfileReducer,
  ui: uiReducer,
  subscription: subscriptionReducer,
  admin: adminReducer,
  quiz: quizReducer,
});

/**
 * Persist configuration
 * Only auth and user data will be persisted to localStorage
 */
const persistConfig = {
  key: "root",
  version: 1,
  storage,
  whitelist: ["auth", "user", "interviewAssessment", "teacherProfile"], // Persist auth, user, assessment feedback cache and teacher profile
  blacklist: [
    "meeting",
    "meetingModules",
    "meetingSection",
    "realtimeConversation",
    "dashboard",
    "ui",
  ], // Don't persist meeting, modules, sections, realtime, dashboard and ui
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * Create Sentry Enhancer for Redux
 * This automatically tracks redux actions and state as breadcrumbs for error context
 */
const sentryReduxEnhancer = Sentry.createReduxEnhancer();

/**
 * Configure Redux store
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for redux-persist
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: import.meta.env.DEV, // Enable Redux DevTools in development
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(sentryReduxEnhancer),
});

/**
 * Create persistor for redux-persist
 */
export const persistor = persistStore(store);

/**
 * Export types for TypeScript
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
